import React from 'react'

/** This hook can be used to provide a signal to other components that you have gone above or below any HTML element on the page.
 *
 * Arguments:
 * offsetInPixelsFromTargetElement: The number of pixels above or below the element to trigger the breakpoint
 * belowTargetElement: If true, the breakpoint will be triggered when the user scrolls below the element. If false, the breakpoint will be triggered when the user scrolls above the element.
 * targetedElementSelector: The HTML element to watch. Defaults to the image uploader container.
 *
 *  For example, if you want to trigger a sticky header when the user scrolls past the header, you can use this hook to provide a signal to the header component.:
 * ```
 *  const isBelowHeader = useBreakpoint(240, true, '#header-container')  # 240px below the top of the HTML element with id 'header-container'
 *  ...
 *  <Header isSticky={isBelowHeader} />
 * ```
 */
export default function useBreakpoint(
  targetedElementSelector,
  offsetInPixelsFromTargetElement = 240,
  belowTargetElement = true
) {
  const [breakpointTriggered, setBreakpointTriggered] = React.useState(false)
  const [yOffSet, setYoffSet] = React.useState(200) // will be used to force re-render

  const elementContainerId = targetedElementSelector
  const domElement = document.querySelector(elementContainerId)
  const targetedElement = belowTargetElement
    ? domElement?.getBoundingClientRect()?.bottom
    : domElement?.getBoundingClientRect()?.top

  React.useEffect(() => {
    window.addEventListener('scroll', () => {
      if (window.pageYOffset > yOffSet) {
        setYoffSet(window.pageYOffset) // forces re-render on scroll event
      }
    })
  }, [yOffSet])

  const elementWatching = React.useMemo(() => {
    return targetedElement
  }, [targetedElement])

  React.useEffect(() => {
    if (!elementWatching) return

    if (elementWatching < offsetInPixelsFromTargetElement) {
      setBreakpointTriggered(true)
    } else {
      setBreakpointTriggered(false)
    }
  }, [elementWatching, offsetInPixelsFromTargetElement])

  return breakpointTriggered
}
